// Onboarding step (native only, docs/06 §2): request the microphone up front
// so answering a call later never silently fails. On Android the WebView's
// getUserMedia prompt can't appear over the keyguard / full-screen incoming
// ring, so a first-time request at answer time is denied and the call dies
// "connection lost". Priming here (foregrounded) settles the grant before any
// call. The browser PWA skips this step entirely — getUserMedia prompts fine
// at call time. Never throws, never blocks onboarding.
//
// On mount we check the CURRENT grant (non-prompting) so a device that
// already granted before — a restore, or re-running onboarding — never sees
// this ask again: 'granted' auto-advances through the same onNext() the
// "Allow" success path uses. Anything else ('prompt'/'unknown', or the check
// itself throwing) fails OPEN to showing the step — only an explicit
// 'granted' ever skips it.

import { useEffect, useState, type ReactNode } from 'react';
import {
  microphonePermissionState,
  requestMicrophonePermission,
} from '@merkury/brain/call/media/micPermission.js';
import { useT } from '../../i18n/index.js';

export function Microphone({
  onNext,
  onBack,
}: {
  onNext: () => void;
  onBack?: () => void;
}): ReactNode {
  const t = useT();
  const [checking, setChecking] = useState(true);
  const [requesting, setRequesting] = useState(false);
  const [denied, setDenied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      let state: Awaited<ReturnType<typeof microphonePermissionState>> = 'unknown';
      try {
        state = await microphonePermissionState();
      } catch {
        // Fail open to showing — never to silently skipping.
      }
      if (cancelled) return;
      if (state === 'granted') onNext();
      else setChecking(false);
    })();
    return () => {
      cancelled = true;
    };
    // Runs once per mount only: re-checking on every render would race the
    // in-flight request below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleEnable(): Promise<void> {
    setRequesting(true);
    let granted = false;
    try {
      granted = await requestMicrophonePermission();
    } finally {
      setRequesting(false);
    }
    // A denial gets a gentle pointer instead of a silent advance — but it
    // never blocks onboarding: Continue stays one tap away.
    if (granted) onNext();
    else setDenied(true);
  }

  // Never flash the request UI before an already-granted device auto-advances.
  if (checking) return null;

  return (
    <main className="mx-auto flex min-h-full w-full max-w-app flex-col gap-6 p-6">
      <h1 className="text-2xl font-semibold text-ink">{t('microphone.title')}</h1>
      <p className="text-sm text-muted">{t('microphone.help')}</p>
      {denied && (
        <p role="status" className="text-sm text-warn">
          {t('microphone.denied')}
        </p>
      )}
      <div className="flex flex-col gap-3">
        {!denied && (
          <button
            type="button"
            disabled={requesting}
            onClick={() => void handleEnable()}
            className="min-h-touch rounded-xl bg-accent px-4 font-medium text-ink disabled:opacity-40 active:bg-accent-press"
          >
            {t('microphone.allow')}
          </button>
        )}
        <div className="flex gap-3">
          {onBack && (
            <button
              type="button"
              onClick={onBack}
              className="min-h-touch flex-1 rounded-xl border border-edge bg-surface px-4 font-medium text-ink"
            >
              {t('common.back')}
            </button>
          )}
          <button
            type="button"
            onClick={onNext}
            className="min-h-touch flex-1 rounded-xl border border-edge bg-surface px-4 font-medium text-ink"
          >
            {denied ? t('common.continue') : t('microphone.skip')}
          </button>
        </div>
      </div>
    </main>
  );
}
